import React, { useEffect, useState } from 'react';
import {
    IonBadge,
    IonButton,
    IonCol,
    IonGrid,
    IonIcon,
    IonRow,
    IonSpinner,
    useIonToast
} from '@ionic/react';
import { eyeOutline, trashOutline, searchOutline, refreshOutline, printOutline } from 'ionicons/icons';
import AdminLayout from '../../layouts/AdminLayout';
import { ShippingBooking, ShippingBookingFilter } from '../../types/shipping-booking.type';
import { fetchAdminShippingBookings, deleteAdminShippingBooking } from '../../services/shipping-booking.service';
import AdminShippingBookingModal from '../../components/Admin/AdminShippingBookingModal';
import Pagination from '../../components/Pagination';
import { printShippingChallan } from '../../services/print.service';
import { useAppContext } from '../../services/app-context.service';
import './AdminPages.scss';

const BDT = new Intl.NumberFormat('en-IN');

const AdminShippingBookingsPage: React.FC = () => {
    const { settings } = useAppContext();
    const [presentToast] = useIonToast();
    const [loading, setLoading] = useState(false);
    const [bookings, setBookings] = useState<ShippingBooking[]>([]);
    const [selectedBooking, setSelectedBooking] = useState<ShippingBooking | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Filters
    const [filter, setFilter] = useState<ShippingBookingFilter>({
        page: 1,
        search: '',
        status: 'all',
        method: 'all'
    });
    const [totalCount, setTotalCount] = useState(0);
    const [lastPage, setLastPage] = useState(1);

    const loadBookings = async (activeFilter: ShippingBookingFilter = filter) => {
        setLoading(true);
        try {
            const res: any = await fetchAdminShippingBookings(activeFilter);
            const responseData = res?.data;
            if (responseData && responseData.data) {
                setBookings(responseData.data);
                setTotalCount(responseData.total ?? responseData.data.length);
                setLastPage(responseData.last_page ?? 1);
            } else if (res && res.data) {
                setBookings(res.data);
                setTotalCount(res.total ?? res.data.length);
                setLastPage(res.last_page ?? 1);
            }
        } catch (err: any) {
            console.error('Failed to load shipping bookings:', err);
            presentToast({
                message: 'Failed to load shipping bookings',
                duration: 2500,
                color: 'danger'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadBookings(filter);
    }, [filter.page, filter.status, filter.method]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        loadBookings({ ...filter, page: 1 });
    };

    const handleOpenBooking = (booking: ShippingBooking) => {
        setSelectedBooking(booking);
        setIsModalOpen(true);
    };

    const handleDelete = async (e: React.MouseEvent, booking: ShippingBooking) => {
        e.stopPropagation();
        if (!window.confirm(`Delete shipping booking #${booking.booking_id}?`)) {
            return;
        }
        try {
            await deleteAdminShippingBooking(booking.id);
            presentToast({
                message: `Booking #${booking.booking_id} deleted`,
                duration: 2500,
                color: 'warning'
            });
            loadBookings(filter);
        } catch (err) {
            presentToast({
                message: 'Failed to delete booking',
                duration: 2500,
                color: 'danger'
            });
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Pending':
                return 'warning';
            case 'Received in China Warehouse':
                return 'primary';
            case 'Shipped To BD':
                return 'secondary';
            case 'BD Warehouse':
                return 'tertiary';
            case 'Delivered':
                return 'success';
            case 'Cancelled':
                return 'danger';
            default:
                return 'medium';
        }
    };

    return (
        <AdminLayout title="Shipping Service Bookings">
            <IonGrid>
                <IonRow>
                    <IonCol size="12">
                        {/* Filter Bar */}
                        <div className="admin-filter-bar" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                            <div>
                                <h3 style={{ margin: 0, color: '#1e293b' }}>
                                    Total: {totalCount} booking{totalCount !== 1 ? 's' : ''}
                                </h3>
                            </div>

                            <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                <input
                                    type="text"
                                    placeholder="Search ID, Tracking, Mark, Phone..."
                                    style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: '0.9rem', minWidth: 240 }}
                                    value={filter.search}
                                    onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                                />

                                <select
                                    style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: '0.9rem' }}
                                    value={filter.status}
                                    onChange={(e) => setFilter({ ...filter, status: e.target.value, page: 1 })}
                                >
                                    <option value="all">All Statuses</option>
                                    <option value="Pending">Pending</option>
                                    <option value="Received in China Warehouse">China Warehouse</option>
                                    <option value="Shipped To BD">Shipped To BD</option>
                                    <option value="BD Warehouse">BD Warehouse</option>
                                    <option value="Delivered">Delivered</option>
                                    <option value="Cancelled">Cancelled</option>
                                </select>

                                <select
                                    style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: '0.9rem' }}
                                    value={filter.method}
                                    onChange={(e) => setFilter({ ...filter, method: e.target.value, page: 1 })}
                                >
                                    <option value="all">All Methods</option>
                                    <option value="Air">Air</option>
                                    <option value="Sea">Sea</option>
                                </select>

                                <button
                                    type="submit"
                                    style={{ padding: '8px 14px', background: '#0a6670', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                                >
                                    <IonIcon icon={searchOutline} /> Search
                                </button>

                                <button
                                    type="button"
                                    onClick={() => {
                                        const reset = { page: 1, search: '', status: 'all', method: 'all' };
                                        setFilter(reset);
                                        loadBookings(reset);
                                    }}
                                    style={{ padding: '8px 12px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer' }}
                                >
                                    <IonIcon icon={refreshOutline} />
                                </button>
                            </form>
                        </div>

                        {/* Bookings Table */}
                        <div className="admin-table-container">
                            {loading && (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <IonSpinner />
                                </div>
                            )}

                            {!loading && bookings.length === 0 && (
                                <div style={{ textAlign: 'center', padding: '50px' }}>
                                    <h3>No Shipping Bookings Found</h3>
                                    <p style={{ color: '#64748b' }}>Customer bookings submitted from the Shipping Service form will appear here.</p>
                                </div>
                            )}

                            {!loading && bookings.length > 0 && (
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Booking ID</th>
                                            <th>Date</th>
                                            <th>Customer</th>
                                            <th>Shipping Mark</th>
                                            <th>Method</th>
                                            <th>Tracking No</th>
                                            <th>Cartons / Wt</th>
                                            <th>Total / Due</th>
                                            <th>Status</th>
                                            <th style={{ textAlign: 'center' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bookings.map((item) => (
                                            <tr
                                                key={item.id}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleOpenBooking(item)}
                                            >
                                                <td>
                                                    <strong>#{item.booking_id}</strong>
                                                </td>
                                                <td>
                                                    {new Date(item.created_at || Date.now()).toLocaleDateString()}
                                                </td>
                                                <td>
                                                    <div>{item.customer_name}</div>
                                                    <small style={{ color: '#64748b' }}>{item.customer_phone}</small>
                                                </td>
                                                <td>
                                                    <span style={{ fontWeight: 600, color: '#0a6670' }}>{item.shipping_mark}</span>
                                                </td>
                                                <td>
                                                    <span style={{ fontSize: '0.85rem' }}>{item.method}</span>
                                                </td>
                                                <td>
                                                    <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>
                                                        {item.tracking_no}
                                                    </code>
                                                </td>
                                                <td>
                                                    {item.carton_count} ctn / {item.weight} kg
                                                </td>
                                                <td>
                                                    <div>{BDT.format(item.total_cost)}/=</div>
                                                    {item.due > 0 && (
                                                        <small style={{ color: '#e11d48', fontWeight: 600 }}>Due: {BDT.format(item.due)}/=</small>
                                                    )}
                                                </td>
                                                <td>
                                                    <IonBadge color={getStatusColor(item.status)}>
                                                        {item.status}
                                                    </IonBadge>
                                                </td>
                                                <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                    <IonButton
                                                        fill="clear"
                                                        size="small"
                                                        color="secondary"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            printShippingChallan(item, settings);
                                                        }}
                                                        title="Print Challan"
                                                    >
                                                        <IonIcon icon={printOutline} />
                                                    </IonButton>
                                                    <IonButton
                                                        fill="clear"
                                                        size="small"
                                                        color="primary"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenBooking(item);
                                                        }}
                                                        title="View / Edit Challan"
                                                    >
                                                        <IonIcon icon={eyeOutline} />
                                                    </IonButton>
                                                    <IonButton
                                                        fill="clear"
                                                        size="small"
                                                        color="danger"
                                                        onClick={(e) => handleDelete(e, item)}
                                                        title="Delete Booking"
                                                    >
                                                        <IonIcon icon={trashOutline} />
                                                    </IonButton>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}

                            {lastPage > 1 && (
                                <Pagination
                                    active={filter.page || 1}
                                    disabled={loading}
                                    buttonCount={lastPage}
                                    onClick={(pageNo: number) => setFilter({ ...filter, page: pageNo + 1 })}
                                />
                            )}
                        </div>
                    </IonCol>
                </IonRow>
            </IonGrid>

            {/* Modal for Challan View/Edit & Customer Options */}
            <AdminShippingBookingModal
                booking={selectedBooking}
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setSelectedBooking(null);
                }}
                onUpdated={() => loadBookings(filter)}
            />
        </AdminLayout>
    );
};

export default AdminShippingBookingsPage;
